// Employee identity contract — the single source of truth for WHO is an employee.
//
// Employee-facing machinery (idle tracking, timeclock, activity logging, presence,
// session lock, exit dispositions, clock-in prompts) must gate on employee IDENTITY
// — WHO the user is — never on token presence or the current route, so a future
// route can never re-leak an employee behavior to a client.
//
// The CMS `users` table holds EMPLOYEES. `guest_teacher` is the one client-type role
// that also lives in `users` (a guest teacher is a school CLIENT, not staff). School
// STUDENTS live in a separate `school_students` table and authenticate with their own
// `school_token`; they never populate the CMS AuthContext `user`. So the only
// non-employee that can reach `user` is a guest_teacher (or a stale employee session
// lingering in shared localStorage — which is an employee identity and is handled the
// same way here).
//
// Mirror of backend/employee_identity.py — keep the two in sync.

// Client-type roles that live in the CMS `users` table but are NOT employees.
export const NON_EMPLOYEE_ROLES = ['guest_teacher'];

// The explicit stored `is_employee` flag WINS when present (IDENTITY M1/M2a — mirrors the backend
// employee_identity.is_employee): a person can be marked not-staff regardless of role (e.g. Taneeka,
// a school guest teacher). When the flag is absent, fall back to the role test so every existing
// record keeps its current meaning.
export const isEmployee = (user) => {
  if (!user) return false;
  if (user.is_employee === true) return true;
  if (user.is_employee === false) return false;
  return !NON_EMPLOYEE_ROLES.includes(String(user.role || '').toLowerCase());
};

// ── Admin-role allowlist mirror (Office Manager role, Oct 8 2026) ───────────────────────────────
// Mirror of backend auth_utils._ADMIN_ROLE_ALLOWLIST (the exact role set require_admin_user /
// has_admin_role admits) PLUS the is_master/is_owner flags. Use this to gate a menu item whose BACKEND
// is require_admin_user — so the link shows for exactly the roles the server admits (e.g. a director
// like Michael sees Complaints + Locations) instead of only privileged admins (the M2c isAdmin gate,
// which hid them from directors the backend allows). NOT the same as is_privileged (master/owner/
// admin/super_admin) — this is the wider role tier that also admits director/manager/collections_*.
// Keep in sync with auth_utils._ADMIN_ROLE_ALLOWLIST.
export const ADMIN_ROLE_ALLOWLIST = [
  'admin', 'super_admin', 'director', 'manager',
  'collections_director', 'collections_manager',
];

// EXACT mirror of backend auth_utils.has_admin_role: is_master OR role-in-allowlist. It does NOT
// honor is_owner (an owner is admitted only if their role is in the allowlist) — keeping the two in
// lockstep so a menu link gated on this never shows for a page the require_admin_user backend refuses.
export const hasAdminRole = (user) => {
  if (!user) return false;
  if (user.is_master) return true;
  return ADMIN_ROLE_ALLOWLIST.includes(String(user.role || '').toLowerCase());
};
